import React, { useState, useCallback } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useWindow } from '../../contexts/WindowContext';
import { useUnifiedData } from '../../contexts/AppDataContext';
import { PremiumTag } from '../Layout/Items/PremiumTag';
import { Space } from '../../types/space';
import SidebarItemDropdown from './SidebarItemDropdown';
import { PlusSignIcon } from 'hugeicons-react';

interface SpacesListProps {
  onSpaceClick: () => void;
}

const SidebarSpacesSkeleton: React.FC = () => (
  <>
    <div className="chat-list-skeleton-container">
      {[1, 2, 3, 4, 5].map((i) => (
        <div key={i} className="chat-list-item-skeleton">
          <div className="chat-list-item-skeleton-title" />
        </div>
      ))}
    </div>
  </>
);


const SpacesList: React.FC<SpacesListProps> = ({ onSpaceClick }) => {
  const auth = useAuth();
  const navigate = useNavigate();
  const { openConfirmationModal } = useWindow();
  const { filteredSpaces, isSpacesLoading, updateSpaceTitle, deleteSpace } = useUnifiedData();
  const [editingSpaceId, setEditingSpaceId] = useState<string | null>(null);
  const [editingSpaceTitle, setEditingSpaceTitle] = useState<string>('');
  const [removingSpaceId, setRemovingSpaceId] = useState<string | null>(null);

  const handleStartRename = useCallback((space: Space) => {
    setEditingSpaceId(space.id);
    setEditingSpaceTitle(space.title || 'Untitled Space');
  }, []);

  const handleSaveRename = useCallback(async (spaceId: string) => {
    if (!editingSpaceTitle.trim()) {
      setEditingSpaceId(null);
      return;
    }

    // Find the current space to compare titles
    const space = filteredSpaces.find(s => s.id === spaceId);
    const currentTitle = space?.title || '';

    // Don't save if title hasn't changed (trimmed comparison)
    if (editingSpaceTitle.trim() === currentTitle.trim()) {
      setEditingSpaceId(null);
      setEditingSpaceTitle('');
      return;
    }

    try {
      await updateSpaceTitle(spaceId, editingSpaceTitle.trim());
      setEditingSpaceId(null);
      setEditingSpaceTitle('');
    } catch (error) {
      console.error('Error renaming space:', error);
    }
  }, [editingSpaceTitle, filteredSpaces, updateSpaceTitle]);

  const handleCancelRename = useCallback(() => {
    setEditingSpaceId(null);
    setEditingSpaceTitle('');
  }, []);

  const handleRenameKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>, spaceId: string) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSaveRename(spaceId);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancelRename();
    }
  }, [handleSaveRename, handleCancelRename]);

  const handleRemoveSpace = useCallback(async (spaceId: string) => {
    const space = filteredSpaces.find(s => s.id === spaceId);
    const spaceTitle = space?.title || 'this space';

    const confirmDelete = () => {
      setRemovingSpaceId(spaceId);
      setTimeout(async () => {
        try {
          await deleteSpace(spaceId);
        } catch (error) {
          console.error('Error deleting space:', error);
        } finally {
          setRemovingSpaceId(null);
        }
      }, 400);
    };

    // Use confirmation modal from WindowContext
    openConfirmationModal(
      `Delete "${spaceTitle}"?`,
      confirmDelete,
      'This action cannot be undone. All chats in this space will also be deleted.'
    );
  }, [filteredSpaces, deleteSpace, openConfirmationModal]);

  const handleStudySpacesClick = (e: React.MouseEvent) => {
    if (auth?.subscription?.plan !== 'ultimate') {
      e.preventDefault();
      navigate('/pricing#study-spaces');
      return;
    }

    localStorage.setItem('hasSeenStudySpaces', 'true');
    navigate('/spaces');
    onSpaceClick();
  };
  
  const maxSpaces = 3;
  const recentSpaces = filteredSpaces.slice(0, maxSpaces);

  return (
    <div className="recent-spaces">
      <div className="spaces-header">
        <div className='spaces-header-left'>
          <h4 className='sidebar-section-title'>Spaces</h4>
        </div>

        {auth?.subscription?.plan !== 'ultimate' ? <PremiumTag /> : (
          <button 
            onClick={handleStudySpacesClick}
            className="view-all"
        >
            View All
          </button>
        )}
      </div>
      {isSpacesLoading ? (
        <SidebarSpacesSkeleton />
      ) : recentSpaces.length > 0 ? (
        <div className="sidebar-section">
          <ul>
            {recentSpaces.map((space: Space) => (
              <li key={space.id} className={`sidebar-item-container ${removingSpaceId === space.id ? 'removing' : ''}`}>
                <NavLink 
                  to={`/spaces/${space.id}`} 
                  className={({isActive}) => `${isActive ? 'active' : ''}`}
                  onClick={onSpaceClick}
                >
                  {editingSpaceId === space.id ? (
                    <input
                      type="text"
                      className="sidebar-item-title-input"
                      value={editingSpaceTitle}
                      onChange={(e) => setEditingSpaceTitle(e.target.value)}
                      onBlur={() => handleSaveRename(space.id)}
                      onKeyDown={(e) => handleRenameKeyDown(e, space.id)}
                      onClick={(e) => e.preventDefault()}
                      autoFocus
                    />
                  ) : (
                    <span
                      className="sidebar-item-title"
                      onDoubleClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleStartRename(space);
                      }}
                    >
                      {space.title}
                    </span>
                  )}

                  <SidebarItemDropdown
                    space={space}
                    onRenameSpace={handleStartRename}
                    onDeleteSpace={handleRemoveSpace}
                  />
                </NavLink>
              </li>
            ))}
        </ul>
        </div>
      ) : (
        <div className="no-spaces-container">
          <button 
            onClick={handleStudySpacesClick}
            className="sidebar-create-space-btn"
          >
            <PlusSignIcon size={14} />
            New Space
          </button>
        </div>
      )}
    </div>
  );
};

export default SpacesList;
